// ============================================================================
// BP-09-04: Forum API Client
// BrightPath Community - Forums API
// ============================================================================

import apiClient from '@/core/lib/api-client';
import {
  ForumCategory,
  ForumCategoriesResponse,
  ForumPost,
  ForumPostListItem,
  ForumPostsResponse,
  ForumPostDetailResponse,
  ForumReply,
  ForumRecentActivityResponse,
  ForumPostsQueryParams,
  ForumSearchParams,
  CreateForumPostInput,
  UpdateForumPostInput,
  CreateForumReplyInput,
} from '../types';

const FORUMS_BASE = '/forums';

// =============================================================================
// Categories
// =============================================================================

export const forumApi = {
  /**
   * Get all forum categories
   */
  async getCategories(): Promise<ForumCategoriesResponse> {
    const response = await apiClient.get<ForumCategoriesResponse>(
      `${FORUMS_BASE}/categories`
    );
    return response.data;
  },

  /**
   * Get a single category by slug
   */
  async getCategoryBySlug(slug: string): Promise<ForumCategory> {
    const response = await apiClient.get<ForumCategory>(
      `${FORUMS_BASE}/categories/${slug}`
    );
    return response.data;
  },

  // ===========================================================================
  // Posts
  // ===========================================================================

  /**
   * Get posts for a category
   */
  async getCategoryPosts(
    categorySlug: string,
    params?: Omit<ForumPostsQueryParams, 'category_slug'>
  ): Promise<ForumPostsResponse> {
    const response = await apiClient.get<ForumPostsResponse>(
      `${FORUMS_BASE}/${categorySlug}/posts`,
      { params }
    );
    return response.data;
  },

  /**
   * Get all posts (across categories)
   */
  async getAllPosts(params?: ForumPostsQueryParams): Promise<ForumPostsResponse> {
    const response = await apiClient.get<ForumPostsResponse>(
      `${FORUMS_BASE}/posts`,
      { params }
    );
    return response.data;
  },

  /**
   * Get a single post with replies
   */
  async getPost(postId: string): Promise<ForumPostDetailResponse> {
    const response = await apiClient.get<ForumPostDetailResponse>(
      `${FORUMS_BASE}/posts/${postId}`
    );
    return response.data;
  },

  /**
   * Create a new post
   */
  async createPost(
    categorySlug: string,
    data: CreateForumPostInput
  ): Promise<ForumPost> {
    const response = await apiClient.post<ForumPost>(
      `${FORUMS_BASE}/${categorySlug}/posts`,
      data
    );
    return response.data;
  },

  /**
   * Update a post
   */
  async updatePost(postId: string, data: UpdateForumPostInput): Promise<ForumPost> {
    const response = await apiClient.patch<ForumPost>(
      `${FORUMS_BASE}/posts/${postId}`,
      data
    );
    return response.data;
  },

  /**
   * Delete a post
   */
  async deletePost(postId: string): Promise<void> {
    await apiClient.delete(`${FORUMS_BASE}/posts/${postId}`);
  },

  /**
   * Like/unlike a post
   */
  async togglePostLike(postId: string): Promise<{ liked: boolean; like_count: number }> {
    const response = await apiClient.post<{ liked: boolean; like_count: number }>(
      `${FORUMS_BASE}/posts/${postId}/like`
    );
    return response.data;
  },

  // ===========================================================================
  // Replies
  // ===========================================================================

  /**
   * Add a reply to a post
   */
  async createReply(
    postId: string,
    data: CreateForumReplyInput
  ): Promise<ForumReply> {
    const response = await apiClient.post<ForumReply>(
      `${FORUMS_BASE}/posts/${postId}/replies`,
      data
    );
    return response.data;
  },

  /**
   * Update a reply
   */
  async updateReply(
    replyId: string,
    content: string
  ): Promise<ForumReply> {
    const response = await apiClient.patch<ForumReply>(
      `${FORUMS_BASE}/replies/${replyId}`,
      { content }
    );
    return response.data;
  },

  /**
   * Delete a reply
   */
  async deleteReply(replyId: string): Promise<void> {
    await apiClient.delete(`${FORUMS_BASE}/replies/${replyId}`);
  },

  /**
   * Like/unlike a reply
   */
  async toggleReplyLike(replyId: string): Promise<{ liked: boolean; like_count: number }> {
    const response = await apiClient.post<{ liked: boolean; like_count: number }>(
      `${FORUMS_BASE}/replies/${replyId}/like`
    );
    return response.data;
  },

  /**
   * Mark reply as solution (for question posts)
   */
  async markAsSolution(replyId: string): Promise<ForumReply> {
    const response = await apiClient.post<ForumReply>(
      `${FORUMS_BASE}/replies/${replyId}/solution`
    );
    return response.data;
  },

  // ===========================================================================
  // Search & Discovery
  // ===========================================================================

  /**
   * Search forum posts
   */
  async searchPosts(params: ForumSearchParams): Promise<ForumPostsResponse> {
    const response = await apiClient.get<ForumPostsResponse>(
      `${FORUMS_BASE}/search`,
      { params }
    );
    return response.data;
  },

  /**
   * Get recent and popular posts
   */
  async getRecentActivity(): Promise<ForumRecentActivityResponse> {
    const response = await apiClient.get<ForumRecentActivityResponse>(
      `${FORUMS_BASE}/recent`
    );
    return response.data;
  },

  /**
   * Get posts by tag
   */
  async getPostsByTag(
    tag: string,
    params?: Omit<ForumPostsQueryParams, 'search'>
  ): Promise<ForumPostsResponse> {
    const response = await apiClient.get<ForumPostsResponse>(
      `${FORUMS_BASE}/tags/${tag}`,
      { params }
    );
    return response.data;
  },
};

export default forumApi;
